import { showAlert, showConfirm } from './tgDialogs';
import React, { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { apiUrl } from './api';
import { formatMicroUsdt, parseUsdtInputToMicro } from './usdtFormat';
import {
  formatCategoryToken,
  normalizeUserCategories,
  orderCategoryLabel,
  partitionProfileCategories,
  SUBCATEGORY_LABELS,
  ALERT_ORDER_CATEGORY_OPTIONS
} from './categoryLabels';
import { orderRefLabel } from './orderRef';
import { FavoriteHeartIcon } from './FavoriteHeartIcon';
import { ThemePicker } from './ThemePicker';
import { payDigitalGoodWithStars } from './starsPay';
import { ProfileExecutorServicesBlock } from './ExecutorServices';
import {
  xpTierForProfileRole,
  xpRingArcCaption,
  xpTierStepIndex,
  xpTierModalRowsForRole,
  xpTierModalHeading,
  XP_RING_TICK_XP,
  XP_MAGNATE_RING_MIN,
  xpRingNeonSegments,
  xpRingSegmentDashArray
} from './xpTiers';

interface Props {
  userId: number;
  token: string;
  onBack: () => void;
  isOwnProfile: boolean;
  /** Роль того, кто смотрит профиль (из мини-приложения) — для кнопки «предложить задание» */
  viewerRole?: string | null;
  /** Если жалоба из карточки заказа — укажите id заказа */
  orderId?: number | null;
  /** После добавления/удаления из избранного (для обновления блока на главной) */
  onFavoritesChanged?: () => void;
  /** Исполнитель: открыть задание заказчика (закрывает профиль, экран задания и отклик) */
  onExecutorOpenCustomerOrder?: (orderId: number) => void;
  /** Заказчик оформил услугу с профиля исполнителя */
  onOrderedService?: (orderId?: number) => void;
  /** Deep link с бота: проскроллить к блоку подписок / рассылки */
  focusSection?: string | null;
  onFocusSectionConsumed?: () => void;
  /** Свой профиль: открыть экран «Условия сервиса» (юридические документы) */
  onOpenLegalTerms?: () => void;
}

function safeToFixedRating(v: any): string {
  const n = Number(v);
  return Number.isFinite(n) ? n.toFixed(2) : '4.50';
}

function formatPercentOrDash(v: unknown): string {
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  return `${Math.round(n)}%`;
}

function formatUsdtOrDash(v: unknown): string {
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  return `${n.toFixed(n < 10 ? 2 : 0).replace(/\.00$/, '')} USDT`;
}

/** Активность на бирже по last_seen_at с бэкенда */
function formatPresenceRu(iso: string | null | undefined): { online: boolean; text: string } {
  if (!iso) return { online: false, text: 'Нет данных о заходе' };
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return { online: false, text: 'Нет данных о заходе' };
  const diff = Date.now() - t;
  if (diff >= 0 && diff < 15 * 60 * 1000) return { online: true, text: 'На бирже' };
  const d = new Date(t);
  const pad = (x: number) => String(x).padStart(2, '0');
  const time = `${pad(d.getHours())}:${pad(d.getMinutes())}`;
  const sod = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const daysDiff = Math.floor((sod(new Date()) - sod(d)) / 86400000);
  if (daysDiff === 0) return { online: false, text: `Был сегодня в ${time}` };
  if (daysDiff === 1) return { online: false, text: `Был вчера в ${time}` };
  if (daysDiff > 1 && daysDiff < 7) return { online: false, text: `Был ${daysDiff} дн. назад в ${time}` };
  return { online: false, text: `Был ${d.toLocaleDateString('ru-RU')} в ${time}` };
}

/** Средние сроки: часы / дни для карточки метрик */
function formatAvgDurationRu(h: unknown): string {
  const n = Number(h);
  if (!Number.isFinite(n) || n < 0) return '—';
  if (n < 1 / 60) return '< 1 мин';
  if (n < 1) {
    const mins = Math.max(1, Math.round(n * 60));
    return `~${mins} мин`;
  }
  if (n < 24) return `~${n.toFixed(1).replace(/\.0$/, '')} ч`;
  const days = n / 24;
  if (days < 1.5) return '~1 день';
  return `~${Math.round(days)} дн.`;
}

/** Дата регистрации на бирже для карточки профиля */
function formatJoinedRu(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return '—';
  return d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

type ProfileMonetaryPeriod = 'day' | 'week' | 'month' | 'halfyear' | 'year';

const PROFILE_MONETARY_PERIOD_TABS: Array<{ key: ProfileMonetaryPeriod; label: string }> = [
  { key: 'day', label: 'День' },
  { key: 'week', label: 'Неделя' },
  { key: 'month', label: 'Месяц' },
  { key: 'halfyear', label: 'Полгода' },
  { key: 'year', label: 'Год' }
];

function earningsCategoryLabel(catKey: string): string {
  const k = String(catKey || '').trim();
  if (!k || k === '__none__') return 'Без категории';
  return formatCategoryToken(k);
}

/** Тонкие линейные иконки (Lucide-подобные), 24×24 */
function HeroIcon({
  children,
  className
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <svg
      className={className || 'fc-profile-hero-v5__icon-svg'}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      {children}
    </svg>
  );
}

/** Подпись кнопки жалобы — по роли того, на кого жалуются */
function profileReportButtonLabel(role: string | undefined): string {
  const r = String(role || '').trim();
  if (r === 'executor') return '⚠️ Пожаловаться на исполнителя';
  if (r === 'customer') return '⚠️ Пожаловаться на заказчика';
  return '⚠️ Пожаловаться на пользователя';
}

/** Тот же каркас, что у остальных экранов мини-приложения + фон как на главной */
function ProfileLayout({ children }: { children: React.ReactNode }) {
  return (
    <div className="App">
      <header className="App-header">
        <div className="fc-profile-page">{children}</div>
      </header>
    </div>
  );
}

const DEFAULT_EXECUTOR_TOP_TIERS: Array<{
  id: string;
  days: number;
  usdt: number;
  stars?: number;
  sku?: string;
}> = [
  { id: '2d', days: 2, usdt: 3, stars: 225, sku: 'executor_top_2d' },
  { id: '4d', days: 4, usdt: 5, stars: 375, sku: 'executor_top_4d' },
  { id: '7d', days: 7, usdt: 10, stars: 750, sku: 'executor_top_7d' }
];

function formatBoostCountdownSec(sec: number) {
  const s = Math.max(0, Math.floor(sec));
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  const r = s % 60;
  const hh = String(h).padStart(2, '0');
  const mm = String(m).padStart(2, '0');
  const rr = String(r).padStart(2, '0');
  if (d > 0) return `${d}д ${hh}:${mm}:${rr}`;
  return `${hh}:${mm}:${rr}`;
}

function ExecutorVipPanel({
  token,
  profile,
  onProfileRefresh
}: {
  token: string;
  profile: any;
  onProfileRefresh: () => Promise<void>;
}) {
  const vipActive = Boolean(profile.executor_vip_active);
  const [boostBusy, setBoostBusy] = useState(false);
  const [channelBusy, setChannelBusy] = useState(false);
  const [vipBusy, setVipBusy] = useState(false);
  const [cfg, setCfg] = useState<any>(null);
  const [displaySec, setDisplaySec] = useState(0);

  useEffect(() => {
    fetch(apiUrl('/api/public/config'))
      .then((r) => r.json())
      .then(setCfg)
      .catch(() => {});
  }, []);

  useEffect(() => {
    setDisplaySec(Math.max(0, Number(profile.executor_boost_seconds_remaining ?? 0)));
  }, [profile.executor_boost_seconds_remaining, profile.executor_boost_active]);

  useEffect(() => {
    if (!profile.executor_boost_active || !vipActive) return;
    const id = window.setInterval(() => setDisplaySec((x) => Math.max(0, x - 1)), 1000);
    return () => window.clearInterval(id);
  }, [profile.executor_boost_active, vipActive]);

  const toggleBoost = async (active: boolean) => {
    setBoostBusy(true);
    try {
      const r = await fetch(apiUrl('/api/users/me/executor-boost'), {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ active })
      });
      const d = await r.json();
      if (!r.ok) {
        showAlert(d.error || 'Не удалось переключить');
        return;
      }
      await onProfileRefresh();
    } catch {
      showAlert('Ошибка сети');
    } finally {
      setBoostBusy(false);
    }
  };

  const buyVipBalance = async () => {
    setVipBusy(true);
    try {
      const status = await payDigitalGoodWithStars(token, 'executor_vip');
      if (status === 'paid') {
        showAlert('VIP исполнителя активирован.');
        await onProfileRefresh();
      } else if (status !== 'cancelled') {
        showAlert('Оплата Stars не завершена. Попробуйте /subscription в боте.');
      }
    } catch (e: any) {
      showAlert(e?.message || 'Ошибка оплаты Stars');
    } finally {
      setVipBusy(false);
    }
  };

  const publishChannel = async () => {
    setChannelBusy(true);
    try {
      const r = await fetch(apiUrl('/api/publish-profile'), {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) {
        showAlert(d.error || 'Не удалось разместить');
      } else {
        showAlert('Анкета отправлена в канал.');
      }
    } catch {
      showAlert('Ошибка сети');
    } finally {
      /** Счётчик в БД мог измениться даже при 500/ошибке ответа — подтягиваем актуальное состояние */
      try {
        await onProfileRefresh();
      } catch {
        /* игнор */
      }
      setChannelBusy(false);
    }
  };

  const poolSec = Number(cfg?.executor_boost_pool_seconds ?? 10 * 86400);
  const vipUsdt = Number(cfg?.executor_vip_month_usdt ?? 20);
  const vipPosts = Number(profile.executor_channel_posts_remaining ?? 0);
  /** Купленные «Посты в канал» — общие для анкеты и услуг (+ старые разовые посты анкеты). */
  const credits =
    Number(profile.executor_service_channel_post_credits ?? 0) +
    Number(profile.executor_channel_post_credits ?? 0);
  const canPublish = (vipActive && vipPosts > 0) || credits > 0;
  const boostDisabled =
    boostBusy || !vipActive || displaySec <= 0;

  return (
    <div id="fc-exec-subscription" className="fc-section-card fc-exec-vip-panel" style={{ marginBottom: 16 }}>
      <h3 className="fc-section-title" style={{ marginTop: 0 }}>
        💎 VIP исполнителя
      </h3>
      <p className="fc-muted" style={{ margin: '0 0 10px', fontSize: 13, lineHeight: 1.45 }}>
        Комиссия с заказов <b>{cfg?.executor_vip_commission_percent ?? 7}%</b>, пул «в ТОП» до{' '}
        {Math.round(poolSec / 86400)} суток (включение в профиле), <b>3</b> поста анкеты и{' '}
        <b>{cfg?.executor_vip_service_channel_posts_per_period ?? 10}</b> постов услуг в канал, до{' '}
        <b>{cfg?.executor_vip_max_responses_per_24h ?? 50}</b> откликов за 24 часа и <b>рассылка заданий</b> на весь
        период. Оплата <b>Telegram Stars</b>: кнопка ниже или <b>/subscription</b> в боте.
      </p>
      {vipActive ? (
        <p style={{ margin: '0 0 12px', fontSize: 13, color: 'var(--fc-text)' }}>
          VIP до{' '}
          {profile.executor_vip_until
            ? new Date(profile.executor_vip_until).toLocaleString('ru-RU', {
                dateStyle: 'short',
                timeStyle: 'short'
              })
            : '—'}
        </p>
      ) : (
        <p className="fc-muted" style={{ margin: '0 0 12px', fontSize: 13 }}>
          Подписка не активна.
        </p>
      )}
      <button
        type="button"
        className="fc-btn fc-btn--secondary fc-btn--block"
        disabled={vipBusy}
        onClick={() => void buyVipBalance()}
      >
        {vipBusy ? '…' : `Оплатить Stars: ${cfg?.executor_vip_month_stars ?? vipUsdt * 75} ⭐ · месяц`}
      </button>

      <h4 className="fc-section-title" style={{ fontSize: 15, marginTop: 16, marginBottom: 6 }}>
        Вывод в блок «ТОП»
      </h4>
      <p className="fc-muted" style={{ margin: '0 0 8px', fontSize: 12, lineHeight: 1.4 }}>
        Пока включено — вы в общей витрине ТОП у заказчиков. Остаток пула:{' '}
        <strong>{formatBoostCountdownSec(displaySec)}</strong>
        {!vipActive ? ' (нужен VIP)' : ''}
        {vipActive && displaySec <= 0 ? ' — лимит на период исчерпан' : ''}
      </p>
      <label
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          fontSize: 14,
          cursor: boostDisabled ? 'not-allowed' : 'pointer',
          opacity: boostDisabled ? 0.55 : 1
        }}
      >
        <input
          type="checkbox"
          checked={Boolean(profile.executor_boost_active)}
          disabled={boostDisabled}
          onChange={(e) => void toggleBoost(e.target.checked)}
        />
        <span>Вывести в ТОП</span>
      </label>
      {profile.executor_boost_active && vipActive && displaySec > 0 ? (
        <p
          className="fc-exec-vip-boost-timer"
          style={{ margin: '10px 0 0', fontSize: 14, fontVariantNumeric: 'tabular-nums' }}
        >
          Таймер пула: {formatBoostCountdownSec(displaySec)}
        </p>
      ) : null}

      <h4 className="fc-section-title" style={{ fontSize: 15, marginTop: 16, marginBottom: 6 }}>
        Канал биржи
      </h4>
      {vipActive ? (
        <p className="fc-muted" style={{ margin: '0 0 8px', fontSize: 12 }}>
          Посты анкеты по VIP: <strong>{vipPosts} из 3</strong>
          {vipPosts <= 0 && credits <= 0 ? (
            <>
              {' '}
              — <strong style={{ color: 'var(--fc-danger)' }}>закончились</strong>. Новые 3 — после продления VIP,
              или купите «Пост в канал» ниже.
            </>
          ) : null}
        </p>
      ) : (
        <p className="fc-muted" style={{ margin: '0 0 8px', fontSize: 12 }}>
          С VIP — 3 поста анкеты за период. Без VIP купите «Пост в канал» ниже или в боте (/subscription).
        </p>
      )}
      <p className="fc-muted" style={{ margin: '0 0 8px', fontSize: 12 }}>
        Купленные посты в канал (для анкеты или услуг): <strong>{credits}</strong>
      </p>
      <button
        type="button"
        className="fc-btn fc-btn--primary fc-btn--block"
        disabled={channelBusy || !canPublish}
        onClick={() => void publishChannel()}
      >
        {channelBusy ? '…' : 'Разместить анкету в канале'}
      </button>
      <button
        type="button"
        className="fc-btn fc-btn--secondary fc-btn--block"
        style={{ marginTop: 8 }}
        disabled={channelBusy}
        onClick={() => {
          void (async () => {
            setChannelBusy(true);
            try {
              const status = await payDigitalGoodWithStars(token, 'service_channel_1');
              if (status === 'paid') {
                await onProfileRefresh();
              } else if (status !== 'cancelled') {
                showAlert('Оплата Stars не завершена.');
              }
            } catch (e: any) {
              showAlert(e?.message || 'Ошибка оплаты Stars');
            } finally {
              setChannelBusy(false);
            }
          })();
        }}
      >
        Купить пост в канал · {cfg?.service_channel_pack_1_price_stars ?? cfg?.executor_channel_post_stars ?? 75} ⭐
      </button>
      <p className="fc-muted" style={{ margin: '12px 0 0', fontSize: 12, lineHeight: 1.4 }}>
        Посты <b>услуг</b> в канал — в разделе «Мои услуги»: по VIP{' '}
        {cfg?.executor_vip_service_channel_posts_per_period ?? 10} за период (осталось{' '}
        <strong>{Number(profile.executor_service_channel_posts_remaining ?? 0)}</strong>). Купленные посты общие для
        анкеты и услуг: 1 / 5 / 10 шт. — {cfg?.service_channel_pack_1_price_stars ?? 75} /{' '}
        {cfg?.service_channel_pack_5_price_stars ?? 350} / {cfg?.service_channel_pack_10_price_stars ?? 650} ⭐ в
        разделе «Бусты».
      </p>
    </div>
  );
}

function alertSubEntries(opt: { subSource?: string }): Array<{ key: string; label: string }> {
  if (!opt.subSource) return [];
  const map = SUBCATEGORY_LABELS[opt.subSource];
  if (!map) return [];
  return Object.entries(map).map(([key, label]) => ({ key, label }));
}

function alertSubToken(main: string, subKey: string) {
  return `sub:${main}:${subKey}`;
}

function ExecutorCategoryAlertsPanel({
  token,
  onProfileRefresh
}: {
  token: string;
  onProfileRefresh: () => Promise<void>;
}) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [buyBusy, setBuyBusy] = useState<string | null>(null);
  const [selected, setSelected] = useState<string[]>([]);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [active, setActive] = useState(false);
  const [activeUntil, setActiveUntil] = useState<string | null>(null);
  const [pendingGiftDays, setPendingGiftDays] = useState(0);
  const [maxCats, setMaxCats] = useState(5);
  const [pack10, setPack10] = useState({ stars: 250, days: 10 });
  const [pack30, setPack30] = useState({ stars: 500, days: 30 });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(apiUrl('/api/users/me/category-alerts'), {
        headers: { Authorization: `Bearer ${token}` }
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || 'Не удалось загрузить');
      setSelected(Array.isArray(d.categories) ? d.categories.map(String) : []);
      setActive(Boolean(d.active));
      setActiveUntil(d.active_until || null);
      setPendingGiftDays(Math.max(0, Number(d.pending_gift_days) || 0));
      setMaxCats(Number(d.max_categories) || 5);
      const p10 = (d.packs || []).find((p: any) => p.sku === 'category_alerts_10d');
      const p30 = (d.packs || []).find((p: any) => p.sku === 'category_alerts_30d');
      if (p10) setPack10({ stars: Number(p10.stars) || 250, days: Number(p10.days) || 10 });
      if (p30) setPack30({ stars: Number(p30.stars) || 500, days: Number(p30.days) || 30 });
    } catch (e: any) {
      console.warn('category-alerts load:', e?.message || e);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    void load();
  }, [load]);

  const toggleToken = (tok: string) => {
    setSelected((prev) => {
      if (prev.includes(tok)) return prev.filter((x) => x !== tok);
      if (prev.length >= maxCats) {
        showAlert(`Можно выбрать не больше ${maxCats} подкатегорий`);
        return prev;
      }
      return [...prev, tok];
    });
  };

  const countUnder = (main: string) =>
    selected.filter((t) => t === main || t.startsWith(`sub:${main}:`)).length;

  const saveCats = async () => {
    setSaving(true);
    try {
      const r = await fetch(apiUrl('/api/users/me/category-alerts'), {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ categories: selected })
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) {
        showAlert(d.error || 'Не удалось сохранить');
        return;
      }
      setSelected(Array.isArray(d.categories) ? d.categories.map(String) : selected);
      setActive(Boolean(d.active));
      setActiveUntil(d.active_until || null);
      setPendingGiftDays(Math.max(0, Number(d.pending_gift_days) || 0));
      showAlert('Выбор сохранён');
    } catch {
      showAlert('Ошибка сети');
    } finally {
      setSaving(false);
    }
  };

  const activateGift = async () => {
    if (!selected.length) {
      showAlert('Сначала выберите хотя бы одну подкатегорию');
      return;
    }
    setSaving(true);
    try {
      const r = await fetch(apiUrl('/api/users/me/category-alerts/activate'), {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ categories: selected })
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) {
        showAlert(d.error || 'Не удалось включить рассылку');
        return;
      }
      setSelected(Array.isArray(d.categories) ? d.categories.map(String) : selected);
      setActive(Boolean(d.active));
      setActiveUntil(d.active_until || null);
      setPendingGiftDays(Math.max(0, Number(d.pending_gift_days) || 0));
      await onProfileRefresh();
      showAlert('Рассылка включена. Таймер подарка запущен.');
    } catch {
      showAlert('Ошибка сети');
    } finally {
      setSaving(false);
    }
  };

  const buy = async (sku: string) => {
    if (!selected.length) {
      showAlert('Сначала выберите хотя бы одну подкатегорию и сохраните');
      return;
    }
    setBuyBusy(sku);
    try {
      const saveR = await fetch(apiUrl('/api/users/me/category-alerts'), {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ categories: selected })
      });
      const saveD = await saveR.json().catch(() => ({}));
      if (!saveR.ok) {
        showAlert(saveD.error || 'Сначала сохраните выбор');
        return;
      }
      const status = await payDigitalGoodWithStars(token, sku);
      if (status === 'paid') {
        showAlert('Рассылка активирована.');
        await load();
        await onProfileRefresh();
      } else if (status !== 'cancelled') {
        showAlert('Оплата Stars не завершена. Попробуйте /subscription в боте.');
      }
    } catch (e: any) {
      showAlert(e?.message || 'Ошибка оплаты Stars');
    } finally {
      setBuyBusy(null);
    }
  };

  return (
    <div
      id="fc-category-alerts"
      className="fc-section-card fc-exec-category-alerts"
      style={{ marginBottom: 16 }}
    >
      <h3 className="fc-section-title" style={{ marginTop: 0 }}>
        📢 Рассылка новых заданий
      </h3>
      <p className="fc-muted" style={{ margin: '0 0 10px', fontSize: 13, lineHeight: 1.45 }}>
        Выберите до <b>{maxCats}</b> подкатегорий. Заказы публикуются по разделу — уведомление
        придёт, если выбрана любая подкатегория этого раздела.
      </p>
      {loading ? (
        <p className="fc-muted" style={{ margin: 0, fontSize: 13 }}>
          Загрузка…
        </p>
      ) : (
        <>
          <p style={{ margin: '0 0 10px', fontSize: 13, color: active ? 'var(--fc-accent)' : 'var(--fc-text-muted)' }}>
            {active && activeUntil
              ? `✅ Активна до ${new Date(activeUntil).toLocaleString('ru-RU', {
                  dateStyle: 'short',
                  timeStyle: 'short'
                })}`
              : pendingGiftDays > 0
                ? `🎁 Подарок: ${pendingGiftDays} дн. — ещё не включена`
                : '❌ Подписка не активна'}
            {' · '}
            Выбрано: {selected.length}/{maxCats}
          </p>
          {pendingGiftDays > 0 && !active ? (
            <p className="fc-muted" style={{ margin: '0 0 10px', fontSize: 12, lineHeight: 1.45 }}>
              Таймер подарка стартует только после «Включить рассылку». Можно включить позже —
              завтра или через неделю.
            </p>
          ) : null}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12 }}>
            {ALERT_ORDER_CATEGORY_OPTIONS.map((c) => {
              const n = countUnder(c.value);
              const open = expanded === c.value;
              const entries = alertSubEntries(c);
              return (
                <div
                  key={c.value}
                  style={{
                    borderRadius: 12,
                    border:
                      n > 0
                        ? '1px solid rgba(var(--fc-accent-rgb), 0.7)'
                        : '1px solid rgba(255,255,255,0.12)',
                    background: n > 0 ? 'rgba(66, 133, 244, 0.18)' : 'rgba(255,255,255,0.04)',
                    overflow: 'hidden'
                  }}
                >
                  <button
                    type="button"
                    className="fc-alert-section-head"
                    onClick={() => setExpanded(open ? null : c.value)}
                  >
                    <span className="fc-alert-section-head__box">{n > 0 ? '✅' : '⬜'}</span>
                    <span className="fc-alert-section-head__label">
                      {c.label}
                      {n > 0 ? ` · ${n}` : ''}
                    </span>
                    <span className="fc-alert-section-head__chev">{open ? '▾' : '›'}</span>
                  </button>
                  {open ? (
                    <div style={{ padding: '0 10px 10px' }}>
                      {entries.length ? (
                        <div className="fc-alert-check-grid">
                          {entries.map((s) => {
                            const tok = alertSubToken(c.value, s.key);
                            const on = selected.includes(tok);
                            return (
                              <button
                                key={tok}
                                type="button"
                                className={`fc-alert-check-btn${on ? ' fc-alert-check-btn--on' : ''}`}
                                onClick={() => toggleToken(tok)}
                              >
                                <span className="fc-alert-check-btn__box">{on ? '✅' : '⬜'}</span>
                                <span className="fc-alert-check-btn__label">{s.label}</span>
                              </button>
                            );
                          })}
                        </div>
                      ) : (
                        <button
                          type="button"
                          className={`fc-alert-check-btn${
                            selected.includes(c.value) ? ' fc-alert-check-btn--on' : ''
                          }`}
                          onClick={() => toggleToken(c.value)}
                        >
                          <span className="fc-alert-check-btn__box">
                            {selected.includes(c.value) ? '✅' : '⬜'}
                          </span>
                          <span className="fc-alert-check-btn__label">Весь раздел</span>
                        </button>
                      )}
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
          {pendingGiftDays > 0 && !active ? (
            <button
              type="button"
              className="fc-btn fc-btn--primary"
              disabled={saving || !selected.length}
              onClick={() => void activateGift()}
              style={{ width: '100%', marginBottom: 8 }}
            >
              {saving ? '…' : `📢 Включить рассылку (${pendingGiftDays} дн.)`}
            </button>
          ) : (
            <button
              type="button"
              className="fc-btn fc-btn--secondary"
              disabled={saving || !selected.length}
              onClick={() => void saveCats()}
              style={{ width: '100%', marginBottom: 8 }}
            >
              {saving ? '…' : 'Сохранить выбор'}
            </button>
          )}
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              type="button"
              className="fc-btn fc-btn--primary"
              disabled={!!buyBusy || !selected.length || (pendingGiftDays > 0 && !active)}
              onClick={() => void buy('category_alerts_10d')}
              style={{ flex: 1 }}
            >
              {buyBusy === 'category_alerts_10d'
                ? '…'
                : `${pack10.stars} ⭐ · ${pack10.days} дн.`}
            </button>
            <button
              type="button"
              className="fc-btn fc-btn--primary"
              disabled={!!buyBusy || !selected.length || (pendingGiftDays > 0 && !active)}
              onClick={() => void buy('category_alerts_30d')}
              style={{ flex: 1 }}
            >
              {buyBusy === 'category_alerts_30d'
                ? '…'
                : `${pack30.stars} ⭐ · ${pack30.days} дн.`}
            </button>
          </div>
          {pendingGiftDays > 0 && !active ? (
            <p className="fc-muted" style={{ margin: '8px 0 0', fontSize: 11, lineHeight: 1.4 }}>
              Покупка Stars станет доступна после включения подарка или если подарок снимут.
            </p>
          ) : null}
        </>
      )}
    </div>
  );
}

function ExecutorTopPurchaseCard({
  token,
  profile,
  onProfileRefresh
}: {
  token: string;
  profile: any;
  onProfileRefresh: () => Promise<void>;
}) {
  const [busy, setBusy] = useState<string | null>(null);
  const [tiers, setTiers] =
    useState<Array<{ id: string; days: number; usdt: number; stars?: number; sku?: string }>>(
      DEFAULT_EXECUTOR_TOP_TIERS
    );

  useEffect(() => {
    fetch(apiUrl('/api/public/config'))
      .then((r) => r.json())
      .then((c) => {
        if (Array.isArray(c.executor_top_tiers) && c.executor_top_tiers.length) {
          setTiers(c.executor_top_tiers);
        }
      })
      .catch(() => {});
  }, []);

  const buy = async (tierId: string) => {
    setBusy(tierId);
    try {
      const status = await payDigitalGoodWithStars(token, `executor_top_${tierId}`);
      if (status === 'paid') {
        showAlert('ТОП активирован.');
        await onProfileRefresh();
      } else if (status !== 'cancelled') {
        showAlert('Оплата Stars не завершена.');
      }
    } catch (e: any) {
      showAlert(e?.message || 'Ошибка оплаты Stars');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="fc-section-card" style={{ marginBottom: 16 }}>
      <h3 className="fc-section-title" style={{ marginTop: 0 }}>
        ⭐ Размещение в блоке «ТОП»
      </h3>
      <p className="fc-muted" style={{ margin: '0 0 10px', fontSize: 13, lineHeight: 1.45 }}>
        Заказчики видят вас в приоритетной ленте на главной. Оплата <b>Telegram Stars</b> — кнопки ниже
        или <b>/subscription</b> в боте.
      </p>
      {profile.top_active && profile.executor_top_until ? (
        <p style={{ margin: '0 0 12px', fontSize: 13, color: 'var(--fc-usdt)' }}>
          Тариф «ТОП на главной» активен до{' '}
          {new Date(profile.executor_top_until).toLocaleString('ru-RU', {
            dateStyle: 'short',
            timeStyle: 'short'
          })}
        </p>
      ) : profile.top_active && profile.executor_vip_active ? (
        <p style={{ margin: '0 0 12px', fontSize: 13, color: 'var(--fc-usdt)' }}>
          Сейчас вы в приоритете у заказчиков за счёт VIP: включён переключатель «Вывести в ТОП» и есть
          остаток пула времени. Отдельный тариф «ТОП на главной» (Stars) не оформлен — фиксированной
          даты окончания по нему нет.
        </p>
      ) : (
        <p className="fc-muted" style={{ margin: '0 0 12px', fontSize: 13 }}>
          Сейчас профиль не отображается в ТОП.
        </p>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {tiers.map((t) => (
          <button
            key={t.id}
            type="button"
            className="fc-btn fc-btn--secondary fc-btn--block"
            disabled={busy !== null}
            onClick={() => void buy(t.id)}
          >
            {busy === t.id ? '…' : `${t.stars ?? t.usdt * 75} ⭐ — ${t.days} дн.`}
          </button>
        ))}
      </div>
    </div>
  );
}

const ProfilePage: React.FC<Props> = ({
  userId,
  token,
  onBack,
  isOwnProfile,
  orderId,
  viewerRole,
  onFavoritesChanged,
  onExecutorOpenCustomerOrder,
  onOrderedService,
  focusSection = null,
  onFocusSectionConsumed,
  onOpenLegalTerms
}) => {
  const [profile, setProfile] = useState<any>(null);
  const [balance, setBalance] = useState<number | null>(null);
  const [transactions, setTransactions] = useState<any[]>([]);
  const [txTotal, setTxTotal] = useState(0);
  const [txPage, setTxPage] = useState(1);
  const [txPageSize, setTxPageSize] = useState(10);
  const [walletDisclaimer, setWalletDisclaimer] = useState<{
    title?: string;
    lines?: string[];
  } | null>(null);
  const [withdrawHint, setWithdrawHint] = useState<{
    title?: string;
    lines?: string[];
  } | null>(null);
  const [currencyNote, setCurrencyNote] = useState<string | null>(null);
  const [minWithdrawStars, setMinWithdrawStars] = useState(10);
  const [withdrawPolicy, setWithdrawPolicy] = useState<{
    admin_review_above_usd: number;
    auto_max_total_per_day_usd: number;
    auto_max_count_per_day: number;
  } | null>(null);
  const [withdrawalRequests, setWithdrawalRequests] = useState<
    Array<{
      id: number;
      amount_usdt_micro: number;
      status: string;
      admin_note: string | null;
      created_at: string;
      processed_at: string | null;
    }>
  >([]);
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [withdrawBusy, setWithdrawBusy] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [showTransactions, setShowTransactions] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showReportForm, setShowReportForm] = useState(false);
  const [reportReason, setReportReason] = useState('');
  const [reportBusy, setReportBusy] = useState(false);
  /** Заказчик → предложить исполнителю своё открытое задание */
  const [offerPanelOpen, setOfferPanelOpen] = useState(false);
  const [offerOrders, setOfferOrders] = useState<any[]>([]);
  const [offerOrdersLoading, setOfferOrdersLoading] = useState(false);
  const [selectedOfferOrderId, setSelectedOfferOrderId] = useState<number | ''>('');
  const [offerSending, setOfferSending] = useState(false);
  const [executorCustomerOrders, setExecutorCustomerOrders] = useState<any[]>([]);
  const [executorCustomerOrdersLoading, setExecutorCustomerOrdersLoading] = useState(false);
  const [executorCustomerOrdersErr, setExecutorCustomerOrdersErr] = useState<string | null>(null);
  const [executorCustomerCatFilter, setExecutorCustomerCatFilter] = useState('');
  const [showXpTierModal, setShowXpTierModal] = useState(false);
  /** Отзывы об исполнителе (чужой профиль) */
  const [executorReviews, setExecutorReviews] = useState<
    Array<{
      id: number;
      stars: number;
      comment: string | null;
      created_at: string;
      order_title: string | null;
      reviewer_name: string;
    }>
  >([]);
  const [executorReviewsTotal, setExecutorReviewsTotal] = useState(0);
  const [executorReviewsOpen, setExecutorReviewsOpen] = useState(false);
  const [executorReviewsPage, setExecutorReviewsPage] = useState(0);
  const [executorReviewsLoading, setExecutorReviewsLoading] = useState(false);  const [executorEarningsPeriod, setExecutorEarningsPeriod] =
    useState<ProfileMonetaryPeriod>('week');
  const [executorEarnings, setExecutorEarnings] = useState<{
    rows: Array<{ category: string; orders_count: number; earned_usdt_micro: string }>;
    totals: { orders_count: number; earned_usdt_micro: string };
  } | null>(null);
  const [executorEarningsLoading, setExecutorEarningsLoading] = useState(false);
  const [executorEarningsError, setExecutorEarningsError] = useState<string | null>(null);
  const [customerSpendingPeriod, setCustomerSpendingPeriod] =
    useState<ProfileMonetaryPeriod>('week');
  const [customerSpending, setCustomerSpending] = useState<{
    rows: Array<{ category: string; orders_count: number; spent_usdt_micro: string }>;
    totals: { orders_count: number; spent_usdt_micro: string };
  } | null>(null);
  const [customerSpendingLoading, setCustomerSpendingLoading] = useState(false);
  const [customerSpendingError, setCustomerSpendingError] = useState<string | null>(null);
  const [profileUserFavorite, setProfileUserFavorite] = useState<boolean | null>(null);
  const [profileUserFavoriteBusy, setProfileUserFavoriteBusy] = useState(false);

  const magnateRingGradUid = React.useId().replace(/:/g, '');

  const canFavoriteThisProfile = useMemo(() => {
    if (isOwnProfile || !token || !profile) return false;
    const vr = String(viewerRole || '').trim();
    const pr = String(profile.role || '').trim();
    return (
      (vr === 'customer' && pr === 'executor') || (vr === 'executor' && pr === 'customer')
    );
  }, [isOwnProfile, token, profile, viewerRole]);

  useEffect(() => {
    if (!canFavoriteThisProfile) {
      setProfileUserFavorite(null);
      return;
    }
    let cancelled = false;
    setProfileUserFavorite(null);
    void (async () => {
      try {
        const res = await fetch(
          apiUrl(
            `/api/favorites/status?entity_type=user&entity_id=${encodeURIComponent(String(userId))}`
          ),
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const d = await res.json();
        if (cancelled) return;
        if (d.success) setProfileUserFavorite(!!d.is_favorite);
        else setProfileUserFavorite(false);
      } catch {
        if (!cancelled) setProfileUserFavorite(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [canFavoriteThisProfile, userId, token]);

  const toggleProfileUserFavorite = useCallback(async () => {
    if (!canFavoriteThisProfile || profileUserFavorite === null || profileUserFavoriteBusy) return;
    setProfileUserFavoriteBusy(true);
    const removing = profileUserFavorite === true;
    try {
      const res = await fetch(apiUrl('/api/favorites'), {
        method: removing ? 'DELETE' : 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ entity_type: 'user', entity_id: userId })
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) {
        showAlert(typeof d.error === 'string' ? d.error : 'Не удалось обновить избранное');
        return;
      }
      setProfileUserFavorite(!removing);
      onFavoritesChanged?.();
    } catch {
      showAlert('Ошибка сети');
    } finally {
      setProfileUserFavoriteBusy(false);
    }
  }, [
    canFavoriteThisProfile,
    profileUserFavorite,
    profileUserFavoriteBusy,
    token,
    userId,
    onFavoritesChanged
  ]);

  const stats =
    profile?.role === 'executor' ? profile?.category_stats_executor : profile?.category_stats_customer;
  const statsEntries: Array<{ key: string; count: number }> = Object.entries(stats || {})
    .map(([k, v]) => ({ key: k, count: Number(v) }))
    .filter((x) => x.key && Number.isFinite(x.count) && x.count > 0)
    .sort((a, b) => b.count - a.count);

  const viewerIsExecutor = String(viewerRole || '').trim() === 'executor';
  const profileIsCustomer = String(profile?.role || '').trim() === 'customer';
  /** Колбэк открытия задания не требуем — иначе блок пропадал, если роль в стейте ещё не подтянулась */
  const canViewCustomerOrders = viewerIsExecutor && profileIsCustomer && !isOwnProfile;

  const executorCustomerCategoryKeys = useMemo(() => {
    const uniq = new Set<string>();
    for (const o of executorCustomerOrders) {
      const c = o?.category != null ? String(o.category).trim() : '';
      if (c) uniq.add(c);
    }
    return Array.from(uniq).sort((a, b) =>
      orderCategoryLabel(a).localeCompare(orderCategoryLabel(b), 'ru')
    );
  }, [executorCustomerOrders]);

  const executorCustomerOrdersFiltered = useMemo(() => {
    if (!executorCustomerCatFilter) return executorCustomerOrders;
    return executorCustomerOrders.filter(
      (o) => String(o?.category || '').trim() === executorCustomerCatFilter
    );
  }, [executorCustomerOrders, executorCustomerCatFilter]);

  const loadWalletSummary = useCallback(async () => {
    try {
      await fetch(apiUrl('/api/wallet/sync-cryptopay'), {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch {
      /* тихо: баланс всё равно подтянем из summary */
    }
    const response = await fetch(apiUrl('/api/wallet/summary'), {
      headers: { Authorization: `Bearer ${token}` }
    });
    const data = await response.json();
    if (data.success) {
      const b = data.balance_usdt_micro ?? data.balance_stars;
      setBalance(typeof b === 'number' ? b : (b != null ? Number(b) : null));
      setTransactions(data.transactions || []);
      setWalletDisclaimer(data.disclaimer || null);
      setCurrencyNote(data.currency_note || null);
      const minW =
        typeof data.min_withdraw_usdt_micro === 'number'
          ? data.min_withdraw_usdt_micro
          : typeof data.min_withdraw_stars === 'number'
            ? data.min_withdraw_stars
            : undefined;
      if (typeof minW === 'number') {
        setMinWithdrawStars(minW);
      }
      setWithdrawHint(data.withdraw_hint || data.real_stars_path || null);
      if (data.withdraw_policy && typeof data.withdraw_policy === 'object') {
        setWithdrawPolicy(data.withdraw_policy);
      }
    }
  }, [token]);

  const loadWithdrawals = useCallback(async () => {
    const response = await fetch(apiUrl('/api/wallet/withdrawals'), {
      headers: { Authorization: `Bearer ${token}` }
    });
    const data = await response.json();
    if (data.success && Array.isArray(data.requests)) {
      setWithdrawalRequests(data.requests);
    }
  }, [token]);

  const reloadProfileUser = useCallback(async () => {
    const res = await fetch(apiUrl(`/api/users/${userId}`), {
      headers: { Authorization: `Bearer ${token}` }
    });
    const data = await res.json();
    if (data.success) setProfile(data.user);
  }, [userId, token]);

  const refreshAfterTopPurchase = useCallback(async () => {
    await reloadProfileUser();
    if (isOwnProfile) {
      try {
        await loadWalletSummary();
      } catch {
        /* ignore */
      }
    }
  }, [reloadProfileUser, isOwnProfile, loadWalletSummary]);

  useEffect(() => {
    fetch(apiUrl(`/api/users/${userId}`), {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.success) {
          setProfile(data.user);
        }
        setLoading(false);
      })
      .catch((err) => {
        console.error('Ошибка загрузки профиля:', err);
        setLoading(false);
      });

    if (isOwnProfile) {
      loadWalletSummary().catch((err) => console.error('Кошелёк:', err));
      loadWithdrawals().catch((err) => console.error('Заявки на вывод:', err));
    }
  }, [userId, token, isOwnProfile, loadWalletSummary, loadWithdrawals]);

  useEffect(() => {
    setExecutorCustomerCatFilter('');
  }, [userId]);

  /** Бот → мини-приложение: открыть свой профиль и прокрутить к рассылке / VIP */
  useEffect(() => {
    if (!focusSection || loading || !profile || !isOwnProfile) return;
    const id =
      focusSection === 'category-alerts'
        ? 'fc-category-alerts'
        : focusSection === 'subscription'
          ? 'fc-exec-subscription'
          : null;
    if (!id) {
      onFocusSectionConsumed?.();
      return;
    }
    let attempts = 0;
    const tryScroll = () => {
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        el.classList.add('fc-focus-highlight');
        window.setTimeout(() => el.classList.remove('fc-focus-highlight'), 2200);
        onFocusSectionConsumed?.();
        return;
      }
      attempts += 1;
      if (attempts < 20) window.setTimeout(tryScroll, 100);
      else onFocusSectionConsumed?.();
    };
    const t = window.setTimeout(tryScroll, 80);
    return () => window.clearTimeout(t);
  }, [focusSection, loading, profile, isOwnProfile, onFocusSectionConsumed]);

  useEffect(() => {
    if (!token) return;
    if (String(viewerRole || '').trim() !== 'executor') return;
    if (!profile || String(profile.role || '').trim() !== 'customer' || isOwnProfile) return;
    let cancelled = false;
    setExecutorCustomerOrdersLoading(true);
    setExecutorCustomerOrdersErr(null);
    void fetch(apiUrl(`/api/orders/open/by-customer/${userId}?limit=50`), {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(async (r) => {
        const d = await r.json().catch(() => ({}));
        if (cancelled) return;
        if (r.ok && d.success && Array.isArray(d.orders)) {
          setExecutorCustomerOrders(d.orders);
          return;
        }
        setExecutorCustomerOrders([]);
        setExecutorCustomerOrdersErr(
          typeof d.error === 'string' ? d.error : r.status === 403 ? 'Доступно только исполнителям' : 'Не удалось загрузить задания'
        );
      })
      .catch(() => {
        if (!cancelled) {
          setExecutorCustomerOrders([]);
          setExecutorCustomerOrdersErr('Не удалось загрузить задания');
        }
      })
      .finally(() => {
        if (!cancelled) setExecutorCustomerOrdersLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token, viewerRole, userId, profile, isOwnProfile]);

  useEffect(() => {
    if (!profile || profile.role !== 'executor') {
      setExecutorReviews([]);
      setExecutorReviewsTotal(0);
      setExecutorReviewsOpen(false);
      setExecutorReviewsPage(0);
      return;
    }
    let cancelled = false;
    setExecutorReviewsLoading(true);
    const pageSize = 10;
    const offset = executorReviewsOpen ? executorReviewsPage * pageSize : 0;
    const limit = executorReviewsOpen ? pageSize : 1;
    fetch(
      apiUrl(
        `/api/reviews/executor/${userId}?limit=${limit}&offset=${offset}`
      ),
      { headers: { Authorization: `Bearer ${token}` } }
    )
      .then((r) => r.json())
      .then((d) => {
        if (cancelled) return;
        if (d.success) {
          setExecutorReviewsTotal(Number(d.total) || 0);
          if (executorReviewsOpen && Array.isArray(d.reviews)) {
            setExecutorReviews(d.reviews);
          } else if (!executorReviewsOpen) {
            setExecutorReviews([]);
          }
        }
      })
      .catch(() => {
        if (!cancelled) {
          setExecutorReviews([]);
          setExecutorReviewsTotal(0);
        }
      })
      .finally(() => {
        if (!cancelled) setExecutorReviewsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [profile, userId, token, executorReviewsOpen, executorReviewsPage]);

  useEffect(() => {
    if (!isOwnProfile || !token || profile?.role !== 'executor') {
      setExecutorEarnings(null);
      setExecutorEarningsError(null);
      setExecutorEarningsLoading(false);
      return;
    }
    let cancelled = false;
    setExecutorEarnings(null);
    setExecutorEarningsLoading(true);
    setExecutorEarningsError(null);
    void (async () => {
      try {
        const res = await fetch(
          apiUrl(
            `/api/users/me/executor-earnings?period=${encodeURIComponent(executorEarningsPeriod)}`
          ),
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const data = await res.json();
        if (!res.ok) {
          throw new Error(typeof data.error === 'string' ? data.error : 'Не удалось загрузить статистику');
        }
        if (!cancelled && data.success) {
          setExecutorEarnings({
            rows: Array.isArray(data.rows) ? data.rows : [],
            totals: data.totals || { orders_count: 0, earned_usdt_micro: '0' }
          });
        }
      } catch (e: unknown) {
        if (!cancelled) {
          setExecutorEarnings(null);
          setExecutorEarningsError(e instanceof Error ? e.message : 'Ошибка загрузки');
        }
      } finally {
        if (!cancelled) setExecutorEarningsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOwnProfile, token, profile?.role, executorEarningsPeriod]);

  useEffect(() => {
    if (!isOwnProfile || !token || profile?.role !== 'customer') {
      setCustomerSpending(null);
      setCustomerSpendingError(null);
      setCustomerSpendingLoading(false);
      return;
    }
    let cancelled = false;
    setCustomerSpending(null);
    setCustomerSpendingLoading(true);
    setCustomerSpendingError(null);
    void (async () => {
      try {
        const res = await fetch(
          apiUrl(
            `/api/users/me/customer-spending?period=${encodeURIComponent(customerSpendingPeriod)}`
          ),
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const data = await res.json();
        if (!res.ok) {
          throw new Error(typeof data.error === 'string' ? data.error : 'Не удалось загрузить статистику');
        }
        if (!cancelled && data.success) {
          setCustomerSpending({
            rows: Array.isArray(data.rows) ? data.rows : [],
            totals: data.totals || { orders_count: 0, spent_usdt_micro: '0' }
          });
        }
      } catch (e: unknown) {
        if (!cancelled) {
          setCustomerSpending(null);
          setCustomerSpendingError(e instanceof Error ? e.message : 'Ошибка загрузки');
        }
      } finally {
        if (!cancelled) setCustomerSpendingLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOwnProfile, token, profile?.role, customerSpendingPeriod]);

  const fetchTransactionsPage = useCallback(
    async (page: number, pageSize: number) => {
      const limit = pageSize;
      const offset = (page - 1) * pageSize;
      const response = await fetch(
        apiUrl(`/api/wallet/transactions?limit=${limit}&offset=${offset}`),
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await response.json();
      if (!data.success) {
        throw new Error(data.error || 'Не удалось загрузить историю');
      }
      setTransactions(data.transactions || []);
      setTxTotal(typeof data.total === 'number' ? data.total : 0);
      setTxPage(page);
      setTxPageSize(pageSize);
    },
    [token]
  );

  const loadTransactions = async () => {
    try {
      await fetchTransactionsPage(1, txPageSize);
      setShowTransactions(true);
    } catch (error) {
      console.error('Ошибка загрузки истории:', error);
      showAlert('Не удалось загрузить историю операций');
    }
  };

  const submitReport = async () => {
    const text = reportReason.trim();
    if (text.length < 10) {
      showAlert('Опишите ситуацию не менее чем в 10 символах.');
      return;
    }
    setReportBusy(true);
    try {
      const res = await fetch(apiUrl('/api/reports'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          reported_user_id: userId,
          order_id: orderId != null ? orderId : null,
          reason: text
        })
      });
      const data = await res.json();
      if (!res.ok) {
        showAlert(data.error || 'Не удалось отправить жалобу');
        return;
      }
      showAlert(data.message || 'Жалоба отправлена');
      setShowReportForm(false);
      setReportReason('');
    } catch (e) {
      console.error(e);
      showAlert('Ошибка сети');
    } finally {
      setReportBusy(false);
    }
  };

  /** Пополнение: открыть бота → Crypto Pay (/topup или /pay сумма в USDT) */
  const handleDeposit = async () => {
    const amount = prompt('Сумма пополнения в USDT (минимум 1). После перехода в бота отправьте /pay с этой суммой:', '10');
    if (!amount) return;

    const numAmount = parseFloat(String(amount).replace(',', '.'));
    if (Number.isNaN(numAmount) || numAmount < 1) {
      showAlert('Введите сумму не меньше 1 USDT');
      return;
    }

    try {
      const response = await fetch(apiUrl('/api/payments/create'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ amount: numAmount })
      });

      const data = await response.json();

      if (data.success && data.paymentUrl) {
        window.open(data.paymentUrl, '_blank');
        showAlert(
          `Откроется бот. В чате с ботом отправьте:\n/pay ${numAmount}\n\nоплатите счёт Crypto Pay (USDT). Баланс в кошельке обновится сам при открытии раздела; в боте — «Обновить баланс» или придёт уведомление после оплаты.`
        );
      } else {
        showAlert('Ошибка: ' + (data.error || 'Не удалось получить ссылку на бота'));
      }
    } catch (error) {
      console.error('Ошибка:', error);
      showAlert('Ошибка соединения с сервером');
    }
  };

  if (loading) {
    return (
      <ProfileLayout>
        <div className="fc-loading">Загрузка...</div>
      </ProfileLayout>
    );
  }
  if (!profile) {
    return (
      <ProfileLayout>
        <div className="fc-error">Пользователь не найден</div>
      </ProfileLayout>
    );
  }

  if (showTransactions) {
    const totalPages = Math.max(1, Math.ceil(txTotal / txPageSize) || 1);
    const safePage = Math.min(txPage, totalPages);
    return (
      <ProfileLayout>
        <div className="fc-topbar">
          <button
            type="button"
            onClick={() => setShowTransactions(false)}
            className="fc-back"
            aria-label="Назад к профилю"
            title="Назад к профилю"
          >
            ←
          </button>
        </div>
        <div className="fc-tx-header">
          <h2 className="fc-page-title" style={{ margin: 0 }}>
            📊 История операций
          </h2>
          <label className="fc-tx-page-size">
            <span className="fc-tx-page-size__label">Показывать</span>
            <select
              className="fc-tx-page-size__select"
              value={txPageSize}
              onChange={async (e) => {
                const nextSize = Number(e.target.value) || 10;
                try {
                  await fetchTransactionsPage(1, nextSize);
                } catch (err) {
                  console.error(err);
                  showAlert('Не удалось загрузить историю');
                }
              }}
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
            </select>
          </label>
        </div>

        {transactions.length === 0 ? (
          <p className="fc-muted">Пока нет операций</p>
        ) : (
          <div className="fc-tx-list">
            {transactions.map((t) => {
              const signed = Number(t.amount_signed ?? t.amount ?? 0);
              const color =
                signed > 0 ? 'var(--fc-usdt)' : signed < 0 ? 'var(--fc-danger)' : 'var(--fc-text-muted)';
              return (
                <div key={t.id} className="fc-transaction-card">
                  <div className="fc-tx-row">
                    <span className="fc-tx-type">{t.display_title || t.type}</span>
                    <span className="fc-tx-amount" style={{ color }}>
                      {t.amount_label ||
                        `${signed > 0 ? '+' : ''}${formatMicroUsdt(signed)} USDT`}
                    </span>
                  </div>
                  <p className="fc-tx-date">
                    {t.created_at ? new Date(t.created_at).toLocaleString('ru-RU') : ''}
                  </p>
                </div>
              );
            })}
          </div>
        )}

        {txTotal > 0 && (
          <div className="fc-tx-pager">
            <button
              type="button"
              className="fc-tx-pager__btn"
              disabled={safePage <= 1}
              onClick={async () => {
                try {
                  await fetchTransactionsPage(safePage - 1, txPageSize);
                } catch (err) {
                  console.error(err);
                }
              }}
            >
              ←
            </button>
            <span className="fc-tx-pager__info">
              {safePage} / {totalPages}
            </span>
            <button
              type="button"
              className="fc-tx-pager__btn"
              disabled={safePage >= totalPages}
              onClick={async () => {
                try {
                  await fetchTransactionsPage(safePage + 1, txPageSize);
                } catch (err) {
                  console.error(err);
                }
              }}
            >
              →
            </button>
          </div>
        )}

        <button
          type="button"
          onClick={() => setShowTransactions(false)}
          className="fc-btn fc-btn--secondary fc-btn--block"
        >
          Назад к профилю
        </button>
      </ProfileLayout>
    );
  }

  const submitWithdrawRequest = async () => {
    const micro = parseUsdtInputToMicro(withdrawAmount);
    if (micro == null || micro < minWithdrawStars) {
      showAlert(`Сумма от ${formatMicroUsdt(minWithdrawStars)} USDT`);
      return;
    }
    if (balance != null && micro > balance) {
      showAlert('Недостаточно USDT на балансе');
      return;
    }
    const ok = await showConfirm(
      `Создать вывод ${formatMicroUsdt(micro)} USDT на @CryptoBot?\n\n` +
        `Вывод с биржи доступен только на @CryptoBot. Дальше вы сами переводите средства куда нужно.\n\n` +
        `До ${withdrawPolicy?.admin_review_above_usd ?? 300} USDT за раз и в пределах дневного лимита — при настроенном Crypto Pay перевод на @CryptoBot сразу, без очереди администратора. ` +
        `Очередь только при превышении этих лимитов или если автовывод на сервере отключён. ` +
        `Если Crypto Pay отклонит мгновенный перевод — баланс не спишется. При ручной заявке средства списываются до отправки; при отказе вернутся на баланс.`
    );
    if (!ok) return;

    setWithdrawBusy(true);
    try {
      const res = await fetch(apiUrl('/api/wallet/withdraw-request'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ amount: withdrawAmount.trim() })
      });
      const data = await res.json();
      if (!res.ok) {
        showAlert(data.error || 'Не удалось создать заявку');
        return;
      }
      showAlert(data.message || 'Заявка создана');
      setWithdrawAmount('');
      setBalance(typeof data.new_balance === 'number' ? data.new_balance : null);
      await loadWalletSummary();
      await loadWithdrawals();
    } catch (e) {
      console.error(e);
      showAlert('Ошибка сети');
    } finally {
      setWithdrawBusy(false);
    }
  };

  const deleteMyProfile = async () => {
    if (!isOwnProfile) return;
    const message =
      'Вы уверены, что хотите удалить анкету?\n\nПосле удаления можно зарегистрироваться заново, но льготы не вернутся.';
    const confirmed = await new Promise<boolean>((resolve) => {
      // @ts-ignore
      const tg = window.Telegram?.WebApp;
      if (tg && typeof tg.showConfirm === 'function') {
        tg.showConfirm(message, (ok: boolean) => resolve(!!ok));
        return;
      }
      resolve(window.confirm(message));
    });
    if (!confirmed) return;
    setDeleteBusy(true);
    try {
      const res = await fetch(apiUrl('/api/users/me/delete'), {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        showAlert(data.error || 'Не удалось удалить анкету');
        return;
      }
      await showAlert(data.message || 'Анкета удалена');
      // После soft reset доступ к мини-аппу закрывается, поэтому делаем reload.
      window.location.reload();
    } catch (e) {
      showAlert('Ошибка сети');
    } finally {
      setDeleteBusy(false);
    }
  };

  const withdrawStatusLabel = (s: string) => {
    if (s === 'pending') return 'В обработке';
    if (s === 'completed') return 'Выполнено';
    if (s === 'rejected') return 'Отклонено';
    return s;
  };

  const canOfferToExecutor =
    viewerRole === 'customer' && !isOwnProfile && profile?.role === 'executor';

  const handleToggleOfferPanel = async () => {
    if (!canOfferToExecutor || !token) return;
    if (offerPanelOpen) {
      setOfferPanelOpen(false);
      return;
    }
    setOfferPanelOpen(true);
    setOfferOrdersLoading(true);
    setSelectedOfferOrderId('');
    try {
      const res = await fetch(apiUrl('/api/orders/my'), {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        setOfferOrders((data.orders || []).filter((o: any) => o.status === 'open'));
      } else {
        setOfferOrders([]);
      }
    } catch {
      setOfferOrders([]);
    } finally {
      setOfferOrdersLoading(false);
    }
  };

  const sendOfferToThisExecutor = async () => {
    if (selectedOfferOrderId === '' || !token) return;
    setOfferSending(true);
    try {
      const res = await fetch(apiUrl(`/api/orders/${selectedOfferOrderId}/offers`), {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ executor_id: userId })
      });
      const data = await res.json();
      if (data.success) {
        showAlert('Предложение отправлено. Исполнителю придёт уведомление в боте.');
        setSelectedOfferOrderId('');
        setOfferPanelOpen(false);
      } else {
        showAlert(data.error || 'Не удалось отправить предложение');
      }
    } catch {
      showAlert('Ошибка сети');
    } finally {
      setOfferSending(false);
    }
  };

  const presence = formatPresenceRu(profile.last_seen_at);
  const rawXp = Math.round(Number(profile.score_balance) || 0);
  const xpTierState = xpTierForProfileRole(rawXp, profile.role);
  const neonRingSegments = xpRingNeonSegments(rawXp);
  const xpRingR = 44;
  const xpCirc = 2 * Math.PI * xpRingR;
  const xpTier = xpTierState.label;
  const xpLine1 = xpRingArcCaption(rawXp);
  const xpTierModalRows = xpTierModalRowsForRole(profile.role);
  const xpModalTitle = xpTierModalHeading(profile.role);
  const xpStepIdx = xpTierStepIndex(rawXp);
  const xpLabelFull = `${xpLine1} · ${xpTier}`;
  const isXpMagnate = rawXp >= XP_MAGNATE_RING_MIN;
  const longterm = Number.isFinite(Number(profile.longterm_partners_count))
    ? Number(profile.longterm_partners_count)
    : 0;
  const repeatOrders = Number.isFinite(Number(profile.repeat_orders_count))
    ? Number(profile.repeat_orders_count)
    : longterm;

  return (
    <ProfileLayout>
      <div className="fc-profile-header-wrap">
        <div className="fc-profile-topbar">
          <button type="button" onClick={onBack} className="fc-back" aria-label="Назад" title="Назад">
            ←
          </button>
          {canFavoriteThisProfile ? (
            <button
              type="button"
              className={`fc-favorite-heart ${profileUserFavorite ? 'fc-favorite-heart--on' : ''}`}
              onClick={() => void toggleProfileUserFavorite()}
              disabled={profileUserFavorite === null || profileUserFavoriteBusy}
              aria-label={profileUserFavorite ? 'Убрать из избранного' : 'В избранное'}
              title={profileUserFavorite ? 'Убрать из избранного' : 'В избранное'}
            >
              <FavoriteHeartIcon filled={profileUserFavorite === true} />
            </button>
          ) : null}
        </div>

        <div className="fc-profile-hero fc-profile-hero--v5">
          <div
            className={
              'fc-profile-hero-v5__card' +
              (profile.role === 'executor' && profile.executor_vip_active ? ' fc-profile-hero-v5__card--exec-vip' : '')
            }
          >
            <div className="fc-profile-hero-v5__bg" aria-hidden />
            <div className="fc-profile-hero-v5__badge-layer" aria-hidden>
              {profile.role === 'executor' && profile.executor_vip_active ? (
                <div className="fc-profile-hero-v5__exec-vip-banner fc-profile-hero-v5__hero-badge fc-profile-hero-v5__hero-badge--vip">
                  VIP
                </div>
              ) : null}
              {profile.role === 'executor' && profile.top_active ? (
                <div className="fc-profile-hero-v5__top-badge fc-profile-hero-v5__hero-badge fc-profile-hero-v5__hero-badge--top">
                  ТОП
                </div>
              ) : null}
            </div>
            <div className="fc-profile-hero-v5__tier-block" aria-label="Статус и дата на бирже">
              <div className="fc-profile-hero-v5__tier-line">
                Статус:{' '}
                <button
                  type="button"
                  className="fc-profile-hero-v5__tier-status-btn"
                  onClick={() => setShowXpTierModal(true)}
                  aria-haspopup="dialog"
                  aria-label="Показать все статусы по XP"
                >
                  {xpTier}
                </button>
              </div>
              <div className="fc-profile-hero-v5__joined-line">
                На бирже: с {formatJoinedRu(profile.registered_at ?? profile.created_at)}
              </div>
            </div>

            <div className="fc-profile-hero-v5__right-col">
              <div
                className={`fc-profile-hero-v5__presence ${
                  presence.online ? 'fc-profile-hero-v5__presence--online' : ''
                }`}
              >
                {!presence.online ? <span className="fc-profile-hero-v5__presence-dot" aria-hidden /> : null}
                {presence.online ? (
                  <span className="fc-profile-hero-v5__presence-online-text">● {presence.text}</span>
                ) : (
                  <span className="fc-profile-hero-v5__presence-offline-text">{presence.text}</span>
                )}
              </div>
              <div
                className={
                  'fc-profile-hero-v5__xp-aside' +
                  (isXpMagnate ? ' fc-profile-hero-v5__xp-aside--magnate' : '')
                }
                title={xpLabelFull}
              >
                {xpLine1}
              </div>
              <span className="fc-visually-hidden">{xpLabelFull}</span>
            </div>

            <div className="fc-profile-hero-v5__avatar-row">
              <div className="fc-profile-hero-v5__avatar-col">
                <div
                  className={
                    'fc-profile-hero-v5__ring-wrap' +
                    (profile.role === 'executor' && profile.top_active
                      ? ' fc-profile-hero-v5__ring-wrap--top'
                      : '')
                  }
                >
                <svg
                  className={`fc-profile-hero-v5__ring-svg${isXpMagnate ? ' fc-profile-hero-v5__ring-svg--magnate' : ''}`}
                  viewBox="0 0 100 100"
                  aria-hidden
                >
                  {isXpMagnate ? (
                    <defs>
                      <linearGradient
                        id={`fc-mg-ring-halo-${magnateRingGradUid}`}
                        x1="18%"
                        y1="12%"
                        x2="82%"
                        y2="88%"
                      >
                        <stop offset="0%" stopColor="#fff8e1" stopOpacity="0.95" />
                        <stop offset="0.28" stopColor="#ffd54f" stopOpacity="0.9" />
                        <stop offset="0.55" stopColor="#f9a825" stopOpacity="0.88" />
                        <stop offset="1" stopColor="#bf6c00" stopOpacity="0.75" />
                      </linearGradient>
                      <linearGradient
                        id={`fc-mg-ring-core-${magnateRingGradUid}`}
                        x1="22%"
                        y1="5%"
                        x2="78%"
                        y2="95%"
                      >
                        <stop offset="0%" stopColor="#fff3c4" />
                        <stop offset="0.2" stopColor="#ffca28" />
                        <stop offset="0.45" stopColor="#ffb300" />
                        <stop offset="0.72" stopColor="#d48c00" />
                        <stop offset="1" stopColor="#7a4a00" />
                      </linearGradient>
                    </defs>
                  ) : null}
                  <circle
                    cx="50"
                    cy="50"
                    r={xpRingR}
                    fill="none"
                    stroke={
                      isXpMagnate ? 'rgba(198, 130, 35, 0.42)' : 'rgba(255,255,255,0.12)'
                    }
                    strokeWidth="9"
                  />
                  {/* Риски по кругу; угол (i+0.5)/n — без метки ровно в 12 часов (на золоте выглядела как «корона» над фото) */}
                  {!isXpMagnate
                    ? Array.from({ length: XP_RING_TICK_XP.length }, (_, i) => {
                        const n = XP_RING_TICK_XP.length;
                        const ang = ((i + 0.5) / n) * 2 * Math.PI - Math.PI / 2;
                        const cr = Math.cos(ang);
                        const sr = Math.sin(ang);
                        const rIn = xpRingR - 5;
                        const rOut = xpRingR + 5.2;
                        return (
                          <line
                            key={`tick-spoke-${i}`}
                            x1={50 + cr * rIn}
                            y1={50 + sr * rIn}
                            x2={50 + cr * rOut}
                            y2={50 + sr * rOut}
                            className="fc-profile-hero-v5__ring-tick"
                          />
                        );
                      })
                    : null}
                  <g transform="rotate(-90 50 50)">
                    {neonRingSegments.map((seg, idx) => {
                      const dash = xpRingSegmentDashArray(seg.fromXp, seg.toXp, xpCirc);
                      const haloStroke = isXpMagnate
                        ? `url(#fc-mg-ring-halo-${magnateRingGradUid})`
                        : seg.glow;
                      const coreStroke = isXpMagnate
                        ? `url(#fc-mg-ring-core-${magnateRingGradUid})`
                        : seg.stroke;
                      return (
                        <Fragment key={`${seg.fromXp}-${seg.toXp}-${idx}`}>
                          <circle
                            cx="50"
                            cy="50"
                            r={xpRingR}
                            fill="none"
                            stroke={haloStroke}
                            strokeWidth="12"
                            strokeOpacity={isXpMagnate ? 0.5 : 0.32}
                            strokeLinecap="butt"
                            strokeDasharray={dash}
                            strokeDashoffset={0}
                            className="fc-profile-hero-v5__ring-seg-halo"
                          />
                          <circle
                            cx="50"
                            cy="50"
                            r={xpRingR}
                            fill="none"
                            stroke={coreStroke}
                            strokeWidth="6.25"
                            strokeLinecap="butt"
                            strokeDasharray={dash}
                            strokeDashoffset={0}
                            className="fc-profile-hero-v5__ring-seg-core"
                          />
                        </Fragment>
                      );
                    })}
                  </g>
                </svg>
                <div className="fc-profile-hero-v5__avatar-inner">
                  <div className="fc-profile-hero-v5__avatar-main">
                    {profile.avatar_url ? (
                      <img src={profile.avatar_url} alt="" className="fc-profile-hero-v5__avatar-img" />
                    ) : (
                      <div className="fc-profile-hero-v5__avatar-ph">
                        {profile.first_name?.[0]}
                        {profile.last_name?.[0]}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              </div>
            </div>

            <h2 className="fc-profile-hero-v5__name">
              {profile.first_name} {profile.last_name}
            </h2>

            <div className="fc-profile-hero-v5__row1">
              {profile.role === 'customer' ? (
                <>
                  <span className="fc-profile-hero-v5__role">
                    Заказчик
                    {profile.is_vip ? <span className="fc-profile-hero-v5__vip"> · VIP</span> : null}
                  </span>
                  <span className="fc-profile-hero-v5__sep" aria-hidden>
                    |
                  </span>
                  <span className="fc-profile-hero-v5__rating">
                    <span aria-hidden>⭐</span>{' '}
                    <span className="fc-profile-hero-v5__rating-val">{safeToFixedRating(profile.rating)}</span>{' '}
                    <span className="fc-profile-hero-v5__ratings-count">
                      ({profile.total_ratings ?? 0}{' '}
                      {(profile.total_ratings ?? 0) === 1 ? 'оценка' : 'оценок'})
                    </span>
                  </span>
                  <span className="fc-profile-hero-v5__sep" aria-hidden>
                    |
                  </span>
                  <div
                    className="fc-profile-hero-v5__handshake"
                    title="Сколько контрагентов работали снова (2+ завершённых сделок)"
                  >
                    <span aria-hidden>🤝</span> Повторные наймы: <strong>{repeatOrders}</strong>
                  </div>
                </>
              ) : null}
              {profile.role === 'executor' ? (
                <>
                  <span className="fc-profile-hero-v5__role">
                    <span aria-hidden>🔨</span> Исполнитель
                    {profile.is_vip ? <span className="fc-profile-hero-v5__vip"> · VIP</span> : null}
                    {profile.executor_vip_active ? (
                      <span className="fc-profile-hero-v5__exec-vip-pill"> · VIP исполнителя</span>
                    ) : null}
                  </span>
                  <span className="fc-profile-hero-v5__sep" aria-hidden>
                    |
                  </span>
                  <span className="fc-profile-hero-v5__rating">
                    <span aria-hidden>⭐</span>{' '}
                    <span className="fc-profile-hero-v5__rating-val">{safeToFixedRating(profile.rating)}</span>{' '}
                    <span className="fc-profile-hero-v5__ratings-count">
                      ({profile.total_ratings ?? 0}{' '}
                      {(profile.total_ratings ?? 0) === 1 ? 'оценка' : 'оценок'})
                    </span>
                  </span>
                  <span className="fc-profile-hero-v5__sep" aria-hidden>
                    |
                  </span>
                  <div
                    className="fc-profile-hero-v5__handshake"
                    title="Сколько контрагентов работали снова (2+ завершённых сделок)"
                  >
                    <span aria-hidden>👥</span> Повторные заказы: <strong>{repeatOrders}</strong>
                  </div>
                </>
              ) : null}
              {profile.role === 'pending' ? (
                <span className="fc-profile-hero-v5__role">⏳ Выбор роли</span>
              ) : null}
            </div>

          </div>
        </div>
      </div>

      {/* Статистика (видна всем) — плавает поверх нижнего края hero; должна идти сразу
          после hero в потоке (без ThemePicker между ними), т.к. наплыв реализован через
          отрицательный margin-top относительно предыдущего соседа по DOM */}
      <div className="fc-stat-grid">
        <div className="fc-stat-card">
          <div className="fc-stat-value">
            {profile.role === 'executor'
              ? (profile.total_finished_as_executor ?? 0)
              : (profile.total_finished_as_customer ?? 0)}
          </div>
          <div className="fc-stat-label">Завершено</div>
        </div>
        <div className="fc-stat-card">
          <div className="fc-stat-value">{rawXp}</div>
          <div className="fc-stat-label">XP</div>
        </div>
      </div>

      {isOwnProfile && <ThemePicker />}

      {/* Анкета */}
      <div className="fc-section-card">
        <h3 className="fc-section-title">📌 Анкета</h3>
        {profile.experience ? (
          <p className="fc-line">
            <strong>Опыт:</strong> {profile.experience}
          </p>
        ) : null}
        {(() => {
          const cats = normalizeUserCategories(profile.categories);
          const { standard, custom } = partitionProfileCategories(cats);
          const std = standard.slice(0, 40);
          const cst = custom.slice(0, 40);
          if (!std.length && !cst.length) return null;
          return (
            <div className="fc-line fc-profile-categories">
              {std.length > 0 ? (
                <div className="fc-profile-category-group">
                  <p className="fc-profile-category-group__title">Категории</p>
                  <div className="fc-profile-category-chips">
                    {std.map((k, i) => (
                      <span className="fc-profile-category-chip" key={`std-${k}-${i}`}>
                        {formatCategoryToken(k)}
                      </span>
                    ))}
                  </div>
                </div>
              ) : null}
              {cst.length > 0 ? (
                <div className="fc-profile-category-group fc-profile-category-group--other">
                  <p className="fc-profile-category-group__title">Другие категории</p>
                  <div className="fc-profile-category-chips">
                    {cst.map((k, i) => (
                      <span className="fc-profile-category-chip" key={`cst-${k}-${i}`}>
                        {formatCategoryToken(k)}
                      </span>
                    ))}
                  </div>
                </div>
              ) : null}
            </div>
          );
        })()}
      </div>

      {/* Надёжность */}
      {profile.role === 'executor' || profile.role === 'customer' ? (
        <>
          <h3 className="fc-section-title fc-reliability-title">🛡️ Надёжность</h3>
          <div className="fc-reliability-grid">
            {profile.role === 'executor' ? (
              <>
                <div className="fc-reliability-card fc-reliability-card--t1">
                  <HeroIcon className="fc-reliability-card__icon">
                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                  </HeroIcon>
                  <div className="fc-reliability-card__value">
                    {formatPercentOrDash(profile.reputation_stats?.executor?.completion_percent)}
                  </div>
                  <div className="fc-reliability-card__label">выполнение</div>
                </div>
                <div className="fc-reliability-card fc-reliability-card--t2">
                  <HeroIcon className="fc-reliability-card__icon">
                    <rect x="3" y="4" width="18" height="18" rx="2" />
                    <path d="M16 2v4M8 2v4M3 10h18" />
                  </HeroIcon>
                  <div className="fc-reliability-card__value">
                    {formatPercentOrDash(profile.reputation_stats?.executor?.deadline_percent)}
                  </div>
                  <div className="fc-reliability-card__label">сдача в срок</div>
                </div>
                <div className="fc-reliability-card fc-reliability-card--t3">
                  <HeroIcon className="fc-reliability-card__icon">
                    <circle cx="12" cy="12" r="10" />
                    <path d="M12 6v6l4 2" />
                  </HeroIcon>
                  <div className="fc-reliability-card__value">
                    {formatAvgDurationRu(profile.reputation_stats?.executor?.avg_delivery_hours)}
                  </div>
                  <div className="fc-reliability-card__label">среднее время сдачи</div>
                </div>
                <div className="fc-reliability-card fc-reliability-card--t4">
                  <HeroIcon className="fc-reliability-card__icon">
                    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                    <circle cx="9" cy="7" r="4" />
                    <path d="M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
                  </HeroIcon>
                  <div className="fc-reliability-card__value">{longterm}</div>
                  <div className="fc-reliability-card__label">повторных клиентов</div>
                </div>
              </>
            ) : null}
            {profile.role === 'customer' ? (
              <>
                <div className="fc-reliability-card fc-reliability-card--t1">
                  <HeroIcon className="fc-reliability-card__icon">
                    <path d="M21.21 15.89A10 10 0 1 1 8 2.83" />
                    <path d="M22 12A10 10 0 0 0 12 2v10z" />
                  </HeroIcon>
                  <div className="fc-reliability-card__value">
                    {formatPercentOrDash(profile.reputation_stats?.customer?.fill_rate_percent)}
                  </div>
                  <div className="fc-reliability-card__label">процент найма</div>
                </div>
                <div className="fc-reliability-card fc-reliability-card--t2">
                  <HeroIcon className="fc-reliability-card__icon">
                    <rect x="2" y="6" width="20" height="12" rx="2" />
                    <path d="M6 10h.01M10 10h.01M14 10h.01" />
                  </HeroIcon>
                  <div className="fc-reliability-card__value">
                    {formatUsdtOrDash(profile.reputation_stats?.customer?.avg_order_usdt)}
                  </div>
                  <div className="fc-reliability-card__label">средний чек</div>
                </div>
                <div className="fc-reliability-card fc-reliability-card--t3">
                  <HeroIcon className="fc-reliability-card__icon">
                    <circle cx="12" cy="12" r="10" />
                    <path d="M12 6v6l4 2" />
                  </HeroIcon>
                  <div className="fc-reliability-card__value">
                    {formatAvgDurationRu(profile.reputation_stats?.customer?.avg_accept_hours)}
                  </div>
                  <div className="fc-reliability-card__label">среднее время оплаты</div>
                </div>
                <div className="fc-reliability-card fc-reliability-card--t4">
                  <HeroIcon className="fc-reliability-card__icon">
                    <path d="m16 16 3-8 3 8c-.87.65-1.92 1-3 1s-2.13-.35-3-1Z" />
                    <path d="m2 16 3-8 3 8c-.87.65-1.92 1-3 1s-2.13-.35-3-1Z" />
                    <path d="M7 21h10" />
                    <path d="M12 3v18" />
                    <path d="M3 7h2c2 0 4-1.79 4-4V5" />
                    <path d="M21 7h-2c-2 0-4-1.79-4-4V5" />
                  </HeroIcon>
                  <div className="fc-reliability-card__value">
                    {formatPercentOrDash(profile.reputation_stats?.customer?.dispute_percent)}
                  </div>
                  <div className="fc-reliability-card__label">арбитраж</div>
                </div>
              </>
            ) : null}
          </div>
        </>
      ) : null}

      {canOfferToExecutor && (
        <div className="fc-section-card fc-profile-offer-card" id="fc-profile-offer-anchor">
          <button
            type="button"
            className="fc-btn fc-btn--primary fc-btn--block fc-btn--sm"
            onClick={() => {
              if (offerPanelOpen) {
                setOfferPanelOpen(false);
                return;
              }
              void handleToggleOfferPanel();
            }}
          >
            {offerPanelOpen ? '▲ Скрыть выбор задания' : '📨 Предложить задание'}
          </button>
          {offerPanelOpen ? (
            <div className="fc-profile-offer-panel">
              {offerOrdersLoading ? (
                <p className="fc-muted" style={{ margin: '8px 0 0', fontSize: 12 }}>
                  Загрузка ваших заданий…
                </p>
              ) : offerOrders.length === 0 ? (
                <p className="fc-muted" style={{ margin: '8px 0 0', fontSize: 12, lineHeight: 1.4 }}>
                  Нет открытых заданий без исполнителя. Создайте задание на главной биржи — тогда сможете
                  предложить его здесь.
                </p>
              ) : (
                <>
                  <label className="fc-profile-offer-label" htmlFor="fc-offer-order-select">
                    Выберите открытое задание
                  </label>
                  <select
                    id="fc-offer-order-select"
                    className="fc-select fc-select--block"
                    value={selectedOfferOrderId === '' ? '' : String(selectedOfferOrderId)}
                    onChange={(e) => {
                      const v = e.target.value;
                      setSelectedOfferOrderId(v === '' ? '' : Number(v));
                    }}
                  >
                    <option value="">— Задание —</option>
                    {offerOrders.map((o: any) => (
                      <option key={o.id} value={o.id}>
                        {orderRefLabel(o.id)} {o.title ? String(o.title).slice(0, 48) : ''} ·{' '}
                        {o.budget != null ? `${formatMicroUsdt(o.budget)} USDT` : '—'}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    className="fc-btn fc-btn--secondary fc-btn--block fc-btn--sm fc-profile-offer-submit"
                    disabled={selectedOfferOrderId === '' || offerSending}
                    onClick={() => void sendOfferToThisExecutor()}
                  >
                    {offerSending ? 'Отправка…' : 'Отправить предложение'}
                  </button>
                </>
              )}
            </div>
          ) : null}

          <ProfileExecutorServicesBlock
            token={token}
            executorUserId={userId}
            canOrder
            embedded
            onOrdered={onOrderedService}
          />
        </div>
      )}

      {canViewCustomerOrders && (
        <div className="fc-section-card" style={{ marginBottom: 14 }}>
          <h3 className="fc-section-title">📋 Открытые задания заказчика</h3>
          <p className="fc-muted" style={{ margin: '0 0 8px', fontSize: 13, lineHeight: 1.45 }}>
            Задания в статусе «Открыто» с доступными откликами. Откройте карточку и нажмите «Откликнуться» — заказчик
            получит уведомление в боте, как при отклике из ленты биржи.
          </p>
          {executorCustomerOrdersLoading ? (
            <p className="fc-muted" style={{ margin: 0 }}>
              Загрузка…
            </p>
          ) : executorCustomerOrdersErr ? (
            <p className="fc-muted" style={{ margin: 0, color: 'var(--fc-danger)' }}>
              {executorCustomerOrdersErr}
            </p>
          ) : executorCustomerOrders.length === 0 ? (
            <p className="fc-muted" style={{ margin: 0 }}>
              Нет открытых заданий, на которые сейчас можно откликнуться.
            </p>
          ) : (
            <>
              <div className="fc-profile-customer-orders-chips" role="tablist" aria-label="Категория заданий">
                <button
                  type="button"
                  role="tab"
                  aria-selected={executorCustomerCatFilter === ''}
                  className={`fc-profile-customer-orders-chip${
                    executorCustomerCatFilter === '' ? ' fc-profile-customer-orders-chip--active' : ''
                  }`}
                  onClick={() => setExecutorCustomerCatFilter('')}
                >
                  Все ({executorCustomerOrders.length})
                </button>
                {executorCustomerCategoryKeys.map((key) => (
                  <button
                    key={key}
                    type="button"
                    role="tab"
                    aria-selected={executorCustomerCatFilter === key}
                    className={`fc-profile-customer-orders-chip${
                      executorCustomerCatFilter === key ? ' fc-profile-customer-orders-chip--active' : ''
                    }`}
                    onClick={() => setExecutorCustomerCatFilter(key)}
                  >
                    {orderCategoryLabel(key)} (
                    {executorCustomerOrders.filter((o) => String(o?.category || '').trim() === key).length})
                  </button>
                ))}
              </div>
              {executorCustomerOrdersFiltered.length === 0 ? (
                <p className="fc-muted" style={{ margin: 0 }}>
                  В этой категории сейчас нет подходящих заданий.
                </p>
              ) : (
                executorCustomerOrdersFiltered.map((o: any) => (
                  <button
                    key={o.id}
                    type="button"
                    className="fc-profile-customer-order-row"
                    onClick={() => {
                      if (typeof onExecutorOpenCustomerOrder === 'function') {
                        onExecutorOpenCustomerOrder(Number(o.id));
                      }
                    }}
                  >
                    <p className="fc-profile-customer-order-row__title">
                      {orderRefLabel(o.id)} {o.title ? String(o.title).slice(0, 80) : 'Задание'}
                    </p>
                    <p className="fc-profile-customer-order-row__meta">
                      {orderCategoryLabel(o.category)} ·{' '}
                      {o.budget != null ? `${formatMicroUsdt(o.budget)} USDT` : '—'} · откликов:{' '}
                      {Number(o.responses_count ?? 0)}
                      {o.responses_limit != null && Number(o.responses_limit) > 0
                        ? ` / ${Number(o.responses_limit)}`
                        : ''}
                      {o.responses_limit != null &&
                      Number(o.responses_limit) > 0 &&
                      Number(o.responses_count ?? 0) >= Number(o.responses_limit)
                        ? ' · лимит откликов заполнен'
                        : ''}
                    </p>
                  </button>
                ))
              )}
            </>
          )}
        </div>
      )}

      {statsEntries.length > 0 && (
        <div className="fc-section-card">
          <h3 className="fc-section-title">📊 Завершено по категориям</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {statsEntries.map((x) => (
              <div key={x.key} className="fc-category-row">
                <span className="fc-category-row__label">{earningsCategoryLabel(x.key)}</span>
                <span className="fc-category-row__count">{x.count}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {isOwnProfile && profile.role === 'executor' && (
        <ExecutorVipPanel token={token} profile={profile} onProfileRefresh={refreshAfterTopPurchase} />
      )}

      {isOwnProfile && profile.role === 'executor' && (
        <ExecutorCategoryAlertsPanel token={token} onProfileRefresh={refreshAfterTopPurchase} />
      )}

      {isOwnProfile && profile.role === 'executor' && (
        <ExecutorTopPurchaseCard token={token} profile={profile} onProfileRefresh={refreshAfterTopPurchase} />
      )}

      {isOwnProfile &&
      profile.role === 'customer' &&
      profile.is_vip &&
      profile.vip_expires_at &&
      new Date(profile.vip_expires_at).getTime() > Date.now() ? (
        <div className="fc-section-card" style={{ marginBottom: 16 }}>
          <h3 className="fc-section-title" style={{ marginTop: 0 }}>
            💎 VIP заказчика
          </h3>
          <p className="fc-muted" style={{ margin: 0, fontSize: 13, lineHeight: 1.45 }}>
            Подписка активна до{' '}
            <strong>
              {new Date(profile.vip_expires_at).toLocaleString('ru-RU', {
                dateStyle: 'short',
                timeStyle: 'short'
              })}
            </strong>
            . После этой даты статус VIP отключится автоматически.
          </p>
        </div>
      ) : null}

      {isOwnProfile && profile.role === 'executor' && (
        <div className="fc-section-card" style={{ marginBottom: 16 }}>
          <h3 className="fc-section-title" style={{ marginTop: 0 }}>
            💹 Заработок по категориям
          </h3>
          <p className="fc-muted" style={{ margin: '0 0 10px', fontSize: 13, lineHeight: 1.4 }}>
            Считается по фактическим выплатам за завершённые заказа (USDT на баланс или через Crypto
            Pay) за выбранный период.
          </p>
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 6,
              marginBottom: 12
            }}
          >
            {PROFILE_MONETARY_PERIOD_TABS.map((t) => (
              <button
                key={t.key}
                type="button"
                className="fc-btn fc-btn--secondary"
                onClick={() => setExecutorEarningsPeriod(t.key)}
                style={{
                  flex: '1 1 auto',
                  minWidth: 52,
                  padding: '7px 8px',
                  fontSize: 12,
                  fontWeight: executorEarningsPeriod === t.key ? 800 : 500,
                  boxShadow:
                    executorEarningsPeriod === t.key
                      ? '0 0 0 2px rgba(129, 212, 250, 0.65)'
                      : undefined
                }}
              >
                {t.label}
              </button>
            ))}
          </div>
          {executorEarningsError && (
            <p className="fc-muted" style={{ color: 'var(--fc-danger)', margin: '0 0 8px' }}>
              {executorEarningsError}
            </p>
          )}
          {executorEarningsLoading && !executorEarnings && !executorEarningsError && (
            <p className="fc-muted">Загрузка…</p>
          )}
          {executorEarnings && !executorEarningsLoading && executorEarnings.rows.length === 0 ? (
            <p className="fc-muted" style={{ margin: 0 }}>
              Нет завершённых выплат за этот период.
            </p>
          ) : null}
          {executorEarnings && executorEarnings.rows.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {executorEarnings.rows.map((row) => (
                <div key={row.category} className="fc-category-row">
                  <span className="fc-category-row__label">{earningsCategoryLabel(row.category)}</span>
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-end',
                      gap: 2,
                      textAlign: 'right'
                    }}
                  >
                    <span className="fc-muted" style={{ fontSize: 12 }}>
                      {row.orders_count}{' '}
                      {row.orders_count === 1 ? 'заказ' : row.orders_count < 5 ? 'заказа' : 'заказов'}
                    </span>
                    <span className="fc-category-row__count">
                      {formatMicroUsdt(row.earned_usdt_micro)} USDT
                    </span>
                  </div>
                </div>
              ))}
              <div
                className="fc-category-row"
                style={{
                  marginTop: 4,
                  borderColor: 'rgba(129, 212, 250, 0.35)',
                  background: 'rgba(129, 212, 250, 0.08)'
                }}
              >
                <span className="fc-category-row__label">Всего</span>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-end',
                    gap: 2,
                    textAlign: 'right'
                  }}
                >
                  <span className="fc-muted" style={{ fontSize: 12 }}>
                    {executorEarnings.totals.orders_count}{' '}
                    {executorEarnings.totals.orders_count === 1
                      ? 'заказ'
                      : executorEarnings.totals.orders_count < 5
                        ? 'заказа'
                        : 'заказов'}
                  </span>
                  <span className="fc-category-row__count">
                    {formatMicroUsdt(executorEarnings.totals.earned_usdt_micro)} USDT
                  </span>
                </div>
              </div>
            </div>
          ) : null}
        </div>
      )}

      {isOwnProfile && profile.role === 'customer' && (
        <div className="fc-section-card" style={{ marginBottom: 16 }}>
          <h3 className="fc-section-title" style={{ marginTop: 0 }}>
            💳 Выплаты по категориям
          </h3>
          <p className="fc-muted" style={{ margin: '0 0 10px', fontSize: 13, lineHeight: 1.4 }}>
            Считается по бюджету завершённых заказов за выбранный период (по дате принятия работы). Деньги
            резервируются на эскроу при оплате после выбора исполнителя; в статистике показан итоговый бюджет
            сделки.
          </p>
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 6,
              marginBottom: 12
            }}
          >
            {PROFILE_MONETARY_PERIOD_TABS.map((t) => (
              <button
                key={t.key}
                type="button"
                className="fc-btn fc-btn--secondary"
                onClick={() => setCustomerSpendingPeriod(t.key)}
                style={{
                  flex: '1 1 auto',
                  minWidth: 52,
                  padding: '7px 8px',
                  fontSize: 12,
                  fontWeight: customerSpendingPeriod === t.key ? 800 : 500,
                  boxShadow:
                    customerSpendingPeriod === t.key
                      ? '0 0 0 2px rgba(129, 212, 250, 0.65)'
                      : undefined
                }}
              >
                {t.label}
              </button>
            ))}
          </div>
          {customerSpendingError && (
            <p className="fc-muted" style={{ color: 'var(--fc-danger)', margin: '0 0 8px' }}>
              {customerSpendingError}
            </p>
          )}
          {customerSpendingLoading && !customerSpending && !customerSpendingError && (
            <p className="fc-muted">Загрузка…</p>
          )}
          {customerSpending && !customerSpendingLoading && customerSpending.rows.length === 0 ? (
            <p className="fc-muted" style={{ margin: 0 }}>
              Нет завершённых заказов с расходами за этот период.
            </p>
          ) : null}
          {customerSpending && customerSpending.rows.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {customerSpending.rows.map((row) => (
                <div key={row.category} className="fc-category-row">
                  <span className="fc-category-row__label">{earningsCategoryLabel(row.category)}</span>
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-end',
                      gap: 2,
                      textAlign: 'right'
                    }}
                  >
                    <span className="fc-muted" style={{ fontSize: 12 }}>
                      {row.orders_count}{' '}
                      {row.orders_count === 1 ? 'заказ' : row.orders_count < 5 ? 'заказа' : 'заказов'}
                    </span>
                    <span className="fc-category-row__count">
                      {formatMicroUsdt(row.spent_usdt_micro)} USDT
                    </span>
                  </div>
                </div>
              ))}
              <div
                className="fc-category-row"
                style={{
                  marginTop: 4,
                  borderColor: 'rgba(129, 212, 250, 0.35)',
                  background: 'rgba(129, 212, 250, 0.08)'
                }}
              >
                <span className="fc-category-row__label">Всего</span>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-end',
                    gap: 2,
                    textAlign: 'right'
                  }}
                >
                  <span className="fc-muted" style={{ fontSize: 12 }}>
                    {customerSpending.totals.orders_count}{' '}
                    {customerSpending.totals.orders_count === 1
                      ? 'заказ'
                      : customerSpending.totals.orders_count < 5
                        ? 'заказа'
                        : 'заказов'}
                  </span>
                  <span className="fc-category-row__count">
                    {formatMicroUsdt(customerSpending.totals.spent_usdt_micro)} USDT
                  </span>
                </div>
              </div>
            </div>
          ) : null}
        </div>
      )}

      {/* Отзывы заказчиков — для исполнителя */}
      {profile.role === 'executor' && executorReviewsTotal > 0 && (
        <div className="fc-section-card" style={{ marginBottom: 16 }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 10,
              marginBottom: executorReviewsOpen ? 10 : 0
            }}
          >
            <h3 className="fc-section-title" style={{ margin: 0 }}>
              💬 Отзывы ({executorReviewsTotal})
            </h3>
            <button
              type="button"
              className="fc-btn fc-btn--ghost fc-btn--sm"
              onClick={() => {
                setExecutorReviewsOpen((v) => {
                  if (v) setExecutorReviewsPage(0);
                  return !v;
                });
              }}
            >
              {executorReviewsOpen ? 'Свернуть' : 'Показать'}
            </button>
          </div>
          {executorReviewsOpen ? (
            <>
              {executorReviewsLoading ? (
                <p className="fc-muted" style={{ margin: 0, fontSize: 13 }}>
                  Загрузка…
                </p>
              ) : (
                <>
                  {executorReviews.map((rev) => (
                    <div key={rev.id} className="fc-review-card">
                      <div className="fc-review-head">
                        <div className="fc-review-stars">
                          {'★'.repeat(rev.stars)}
                          {'☆'.repeat(5 - rev.stars)}
                        </div>
                        <p className="fc-review-meta">
                          {rev.reviewer_name}
                          {rev.created_at
                            ? ` · ${new Date(rev.created_at).toLocaleDateString('ru-RU')}`
                            : ''}
                        </p>
                      </div>
                      {rev.order_title && (
                        <p className="fc-review-order">{rev.order_title}</p>
                      )}
                      {rev.comment && <p className="fc-review-text">{rev.comment}</p>}
                    </div>
                  ))}
                  {executorReviewsTotal > 10 ? (
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 8,
                        marginTop: 4
                      }}
                    >
                      <button
                        type="button"
                        className="fc-btn fc-btn--ghost fc-btn--sm"
                        disabled={executorReviewsPage <= 0 || executorReviewsLoading}
                        onClick={() => setExecutorReviewsPage((p) => Math.max(0, p - 1))}
                      >
                        ←
                      </button>
                      <span style={{ fontSize: 12, color: 'var(--fc-text-muted)' }}>
                        {executorReviewsPage + 1} /{' '}
                        {Math.max(1, Math.ceil(executorReviewsTotal / 10))}
                      </span>
                      <button
                        type="button"
                        className="fc-btn fc-btn--ghost fc-btn--sm"
                        disabled={
                          (executorReviewsPage + 1) * 10 >= executorReviewsTotal ||
                          executorReviewsLoading
                        }
                        onClick={() => setExecutorReviewsPage((p) => p + 1)}
                      >
                        →
                      </button>
                    </div>
                  ) : null}
                </>
              )}
            </>
          ) : null}
        </div>
      )}

      {isOwnProfile &&
        (profile.free_customer_postings_left != null ||
          profile.free_executor_fee_waivers_left != null) && (
          <p className="fc-freemium-hint">
            {profile.role === 'customer' && (
              <>
                Размещение заданий <strong>бесплатное</strong> — до{' '}
                <strong>
                  {profile.customer_daily_orders_limit_effective ?? profile.customer_daily_orders_limit ?? 5}
                </strong>{' '}
                в сутки. Слотов «ТОП задания»: <strong>{Number(profile.order_top_slots_left ?? 0)}</strong>, «Срочно»:{' '}
                <strong>{Number(profile.urgent_slots_left ?? 0)}</strong>. Эскроу оплачивается после выбора
                исполнителя.{' '}
              </>
            )}
            {profile.role === 'executor' && (
              <>
                Сделок без комиссии платформы осталось:{' '}
                <strong>{profile.free_executor_fee_waivers_left ?? '—'}</strong>.{' '}
              </>
            )}
            {profile.role === 'pending' && (
              <>
                Стартовые подарки: заказчику — <strong>{profile.free_customer_order_top_default ?? 3}</strong> слота
                «ТОП задания»; исполнителю — <strong>{profile.free_executor_fee_waivers_left ?? 5}</strong> сделок без
                комиссии, размещения услуг и <strong>{profile.free_executor_category_alerts_days ?? 3}</strong> дня
                рассылки заданий.
              </>
            )}
          </p>
        )}

      {/* БАЛАНС - только для владельца */}
      {isOwnProfile && (
        <div className="fc-wallet-section">
          <h3 className="fc-wallet-title">💰 Мой кошелёк</h3>
          {currencyNote && <p className="fc-muted">{currencyNote}</p>}
          {walletDisclaimer?.lines && (
            <div className="fc-disclaimer">
              <strong style={{ color: 'var(--fc-text)' }}>
                {walletDisclaimer.title || 'Важно'}
              </strong>
              {walletDisclaimer.lines.map((line: string, i: number) => (
                <p key={i} style={{ margin: '6px 0 0' }}>
                  {line}
                </p>
              ))}
            </div>
          )}
          {withdrawHint?.lines && withdrawHint.lines.length > 0 && (
            <div className="fc-withdraw-hint">
              <strong style={{ color: 'var(--fc-accent-strong)' }}>
                {withdrawHint.title || 'Вывод USDT'}
              </strong>
              {withdrawHint.lines.map((line: string, i: number) => (
                <p key={i} style={{ margin: '8px 0 0' }}>
                  {line}
                </p>
              ))}
            </div>
          )}
          <div className="fc-balance-row">
            <span className="fc-muted">Внутренний баланс:</span>
            <span className="fc-balance-val">
              {balance != null ? `${formatMicroUsdt(balance)} USDT` : '0 USDT'}
            </span>
          </div>
          
          <div className="fc-wallet-grid">
            <button type="button" onClick={handleDeposit} className="fc-btn-deposit">
              💳 Пополнить
            </button>
            <button type="button" onClick={loadTransactions} className="fc-btn-history">
              📋 История
            </button>
          </div>

          <div className="fc-withdraw-panel">
            <p className="fc-withdraw-title">💸 Вывод USDT → @CryptoBot</p>
            <p className="fc-hint">
              Вывод доступен только на кошелёк <strong>@CryptoBot</strong> в Telegram. Затем из CryptoBot
              вы можете вывести средства на любой удобный кошелёк или карту. Минимум{' '}
              {formatMicroUsdt(minWithdrawStars)} USDT.
              {withdrawPolicy ? (
                <>
                  {' '}
                  Автоматически — до <strong>{withdrawPolicy.admin_review_above_usd} USDT</strong> за раз,
                  не более <strong>{withdrawPolicy.auto_max_count_per_day}</strong> раз и{' '}
                  <strong>{withdrawPolicy.auto_max_total_per_day_usd} USDT</strong> суммарно за сутки;
                  иначе — очередь администратора.
                </>
              ) : null}
            </p>
            <div className="fc-withdraw-input-row">
              <input
                type="text"
                inputMode="decimal"
                pattern="[0-9]*[.,]?[0-9]*"
                placeholder="например 10 или 1.1"
                value={withdrawAmount}
                onChange={(e) => setWithdrawAmount(e.target.value)}
                className="fc-input-dark"
                style={{ flex: 1 }}
              />
              <button
                type="button"
                onClick={submitWithdrawRequest}
                disabled={withdrawBusy}
                className="fc-withdraw-submit"
              >
                {withdrawBusy ? '…' : 'Заявка'}
              </button>
            </div>
            <a
              href="https://t.me/CryptoBot"
              target="_blank"
              rel="noopener noreferrer"
              className="fc-link"
              style={{ fontSize: '13px', display: 'inline-block', marginTop: '6px' }}
            >
              Справка: @CryptoBot
            </a>
          </div>

          {withdrawalRequests.length > 0 && (
            <div className="fc-withdraw-list">
              <p className="fc-withdraw-list-title">Мои заявки</p>
              {withdrawalRequests.map((w) => (
                <div key={w.id} className="fc-withdraw-row">
                  <span>
                    {formatMicroUsdt(w.amount_usdt_micro)} USDT ·{' '}
                    {withdrawStatusLabel(w.status)}
                  </span>
                  <span className="fc-withdraw-date">
                    {w.created_at ? new Date(w.created_at).toLocaleString('ru-RU') : ''}
                  </span>
                  {w.admin_note && (
                    <p className="fc-withdraw-note">{w.admin_note}</p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {!isOwnProfile && (
        <div id="fc-profile-report-anchor" style={{ marginBottom: 16 }}>
          {!showReportForm ? (
            <button
              type="button"
              onClick={() => setShowReportForm(true)}
              className="fc-report-btn"
            >
              {profileReportButtonLabel(profile.role)}
            </button>
          ) : (
            <div className="fc-report-form">
              <label className="fc-muted" style={{ display: 'block', marginBottom: 8 }}>
                Опишите нарушение (от 10 символов). Жалоба уходит модерации в админ-бот.
              </label>
              <textarea
                value={reportReason}
                onChange={(e) => setReportReason(e.target.value)}
                rows={4}
                className="fc-textarea-dark"
                placeholder="Например: нарушение правил сделки, оскорбления…"
              />
              <div className="fc-report-actions">
                <button
                  type="button"
                  onClick={submitReport}
                  disabled={reportBusy}
                  className="fc-report-submit"
                >
                  {reportBusy ? '…' : 'Отправить'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowReportForm(false);
                    setReportReason('');
                  }}
                  className="fc-report-cancel"
                >
                  Отмена
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {isOwnProfile && onOpenLegalTerms ? (
        <button
          type="button"
          onClick={onOpenLegalTerms}
          className="fc-btn fc-btn--secondary fc-btn--block"
        >
          📄 Условия сервиса
        </button>
      ) : null}

      {isOwnProfile && (
        <button
          type="button"
          onClick={deleteMyProfile}
          disabled={deleteBusy}
          className="fc-delete-profile"
        >
          {deleteBusy ? '…' : '🗑️ Удалить анкету'}
        </button>
      )}

      <button type="button" onClick={onBack} className="fc-btn fc-btn--secondary fc-btn--block">
        ← Назад
      </button>

      {showXpTierModal ? (
        <div
          className="fc-xp-tier-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="fc-xp-tier-modal-title"
        >
          <button
            type="button"
            className="fc-xp-tier-modal__backdrop"
            aria-label="Закрыть"
            onClick={() => setShowXpTierModal(false)}
          />
          <div className="fc-xp-tier-modal__panel">
            <div className="fc-xp-tier-modal__head">
              <h3 id="fc-xp-tier-modal-title" className="fc-xp-tier-modal__title">
                {xpModalTitle}
              </h3>
              <button
                type="button"
                className="fc-xp-tier-modal__close"
                onClick={() => setShowXpTierModal(false)}
                aria-label="Закрыть"
              >
                ×
              </button>
            </div>
            <ol className="fc-xp-tier-modal__list" start={1}>
              {xpTierModalRows.map((row, i) => (
                <li
                  key={`${row.title}-${row.range}`}
                  className={
                    i === xpStepIdx
                      ? 'fc-xp-tier-modal__item fc-xp-tier-modal__item--current'
                      : 'fc-xp-tier-modal__item'
                  }
                >
                  <span className="fc-xp-tier-modal__item-title">
                    {i + 1}. {row.title}{' '}
                    <span className="fc-xp-tier-modal__item-range">({row.range})</span>
                  </span>
                  <span className="fc-xp-tier-modal__item-desc">
                    {' — '}
                    {row.description}
                    {i === xpStepIdx ? ' Твой текущий статус.' : ''}
                  </span>
                </li>
              ))}
            </ol>
          </div>
        </div>
      ) : null}
    </ProfileLayout>
  );
};

export default ProfilePage;